// GST (§5.3), FATCA (§5.4), Favourites (§5.5) and Nominee (§5.9) — the four
// simpler Bank Account screens, consolidated. OTP through the auth chain for
// add/remove/edit; favourites need none.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faStar } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  gstRegistrations, lookupGstin, addGstin, removeGstin, setPrimaryGstin, gstInvoices,
  fatca as getFatca, updateFatca, fatcaReview,
  favourites as getFavourites, renameFavourite, removeFavourite,
  accountNominee, accountFacts,
} from '@/api/bankServices';
import { runBankClock } from '@/mock/phase4';
import type { FatcaRecord, GstRegistration } from '@/types/phase4.types';
import { usePaymentAuth } from '@/pages/Pay/components/usePaymentAuth';
import PaymentAuthSheets from '@/pages/Pay/components/PaymentAuthSheets';
import { formatIndianCurrency } from '@/utils/currency';
import ListState from '../components/ListState';
import '../phase4.css';

const Header = ({ title, onBack }: { title: string; onBack: () => void }) => (
  <div className="nova-p4-header">
    <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={onBack}>
      <FontAwesomeIcon icon={faArrowLeft} />
    </button>
    <h2>{title}</h2>
  </div>
);

// ============================================================ GST (§5.3)

export const Gst = () => {
  const navigate = useNavigate();
  const [regs, setRegs] = useState<GstRegistration[] | null>(null);
  const [invoices, setInvoices] = useState<Awaited<ReturnType<typeof gstInvoices>>>([]);
  const [gstin, setGstin] = useState('');
  const [looked, setLooked] = useState<Awaited<ReturnType<typeof lookupGstin>> | null>(null);
  const [error, setError] = useState('');
  const [product, setProduct] = useState('');

  const load = () => {
    runBankClock();
    gstRegistrations().then(setRegs).catch(() => setRegs([]));
    gstInvoices().then(setInvoices).catch(() => setInvoices([]));
    setProduct(accountFacts().productType);
  };
  useEffect(load, []);

  const auth = usePaymentAuth(async () => {
    try {
      await addGstin(gstin.toUpperCase());
      toast.success('GSTIN added. Applies to charges from today onwards.');
      setGstin(''); setLooked(null);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add GSTIN.');
    }
  });

  const doLookup = async () => {
    setError('');
    try {
      setLooked(await lookupGstin(gstin.toUpperCase()));
    } catch (err) {
      setLooked(null);
      setError(err instanceof Error ? err.message : 'Invalid GSTIN.');
    }
  };

  return (
    <div className="nova-p4-page">
      <Header title="Update GST" onBack={() => navigate('/profile/bank')} />
      <div className="nova-p4-banner blue">
        Without your GSTIN on the account, you can't claim input tax credit on GST paid to the bank.
        {product === 'SAVINGS' && ' Only for GST-registered businesses.'}
      </div>

      <div className="nova-p4-card">
        <h3>Add GSTIN</h3>
        <div className="nova-p4-field">
          <label htmlFor="gstin">15-character GSTIN</label>
          <input id="gstin" className="form-control" maxLength={15} value={gstin} onChange={(e) => { setGstin(e.target.value.toUpperCase()); setLooked(null); }} placeholder="27ABCDE1234F1Z5" />
          {error && <div className="err">{error}</div>}
        </div>
        {!looked && <button type="button" className="nova-p4-secondary" disabled={gstin.length !== 15} onClick={() => void doLookup()}>Fetch details</button>}
        {looked && (
          <>
            <div className="nova-p4-moneyline muted"><span>Legal name</span><span>{looked.legalName}</span></div>
            <div className="nova-p4-moneyline muted"><span>Trade name</span><span>{looked.tradeName}</span></div>
            <div className="nova-p4-moneyline muted"><span>State</span><span>{looked.stateName} ({looked.stateCode})</span></div>
            <button type="button" className="nova-p4-primary" style={{ marginTop: 12 }} onClick={() => void auth.start('NOVABANK', `Add GSTIN ${gstin}`)}>Add (OTP)</button>
          </>
        )}
      </div>

      <ListState data={regs} error={null} onRetry={load} empty={<span>No GSTIN on the account yet.</span>}>
        {(list) => list.map((g) => (
          <div key={g.gstin} className="nova-p4-card">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className="nova-p4-row-title">{g.gstin}</div>
              {g.primary && <span className="nova-p4-status ok">Primary for invoicing</span>}
            </div>
            <div className="nova-p4-row-sub">{g.tradeName} · {g.stateName}</div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              {!g.primary && <button type="button" className="nova-p4-link" onClick={() => void setPrimaryGstin(g.gstin).then(load)}>Make primary</button>}
              <button type="button" className="nova-p4-link" style={{ color: '#c0392b' }} onClick={() => { if (window.confirm('Future invoices will be B2C.')) void removeGstin(g.gstin).then(load); }}>Remove</button>
            </div>
          </div>
        ))}
      </ListState>

      {invoices.length > 0 && (
        <>
          <h3 className="nova-p4-row-title" style={{ margin: '16px 0 10px' }}>GST invoices</h3>
          {invoices.map((inv) => (
            <div key={inv.month} className="nova-p4-card">
              <div className="nova-p4-row-title">{inv.month}</div>
              <div className="nova-p4-moneyline muted"><span>Charges</span><span>{formatIndianCurrency(inv.chargesRupees)}</span></div>
              {inv.igst > 0
                ? <div className="nova-p4-moneyline muted"><span>IGST 18%</span><span>{formatIndianCurrency(inv.igst)}</span></div>
                : <><div className="nova-p4-moneyline muted"><span>CGST 9%</span><span>{formatIndianCurrency(inv.cgst)}</span></div>
                    <div className="nova-p4-moneyline muted"><span>SGST 9%</span><span>{formatIndianCurrency(inv.sgst)}</span></div></>}
              <div className="nova-p4-row-sub">{inv.gstin}</div>
            </div>
          ))}
        </>
      )}
      <PaymentAuthSheets authState={auth.authState} approvalLine="Add GSTIN" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </div>
  );
};

// ============================================================ FATCA (§5.4)

export const Fatca = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<FatcaRecord | null>(null);
  const [pending, setPending] = useState<string | undefined>();
  const [foreign, setForeign] = useState(false);
  const [usPerson, setUsPerson] = useState(false);
  const [country, setCountry] = useState('');
  const [tin, setTin] = useState('');
  const [noTinReason, setNoTinReason] = useState('');
  const [declared, setDeclared] = useState(false);

  const load = () => {
    runBankClock();
    getFatca().then((f) => { setState(f); setForeign(f.foreignTaxResident); setUsPerson(f.usPerson); }).catch(() => undefined);
    setPending(fatcaReview().pending);
  };
  useEffect(load, []);

  const auth = usePaymentAuth(async () => {
    try {
      const res = await updateFatca({
        foreignTaxResident: foreign,
        countries: foreign ? [{ country: country || 'USA', tin: tin || undefined, noTinReason: noTinReason || undefined }] : [],
        countryOfBirth: state?.countryOfBirth ?? 'India',
        citizenship: state?.citizenship ?? 'India',
        usPerson,
      });
      toast.success(res.review ? `Under review · ${res.review}` : 'FATCA updated');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update.');
    }
  });

  return (
    <div className="nova-p4-page">
      <Header title="FATCA Update" onBack={() => navigate('/profile/bank')} />
      {pending && <div className="nova-p4-banner amber">A change is under review ({pending}). We may contact you for documents. Update is disabled until the bank decides.</div>}

      {state && (
        <div className="nova-p4-card">
          <h3>On record</h3>
          <div className="nova-p4-moneyline muted"><span>Tax resident outside India</span><span>{state.foreignTaxResident ? 'Yes' : 'No'}</span></div>
          {state.countries.map((c, i) => <div key={i} className="nova-p4-moneyline muted"><span>{c.country}</span><span>{c.tin ?? `No TIN — ${c.noTinReason}`}</span></div>)}
          <div className="nova-p4-moneyline muted"><span>Country of birth</span><span>{state.countryOfBirth}</span></div>
          <div className="nova-p4-moneyline muted"><span>Citizenship</span><span>{state.citizenship}</span></div>
          <div className="nova-p4-moneyline muted"><span>US person</span><span>{state.usPerson ? 'Yes' : 'No'}</span></div>
          <div className="nova-p4-moneyline muted"><span>Last updated</span><span>{new Date(state.lastUpdatedAt).toLocaleDateString('en-IN')}</span></div>
        </div>
      )}

      <div className="nova-p4-card">
        <h3>Edit</h3>
        <div className="nova-p4-field">
          <label htmlFor="f_foreign">Tax resident outside India?</label>
          <select id="f_foreign" className="form-select" value={foreign ? 'yes' : 'no'} onChange={(e) => setForeign(e.target.value === 'yes')} disabled={!!pending}>
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </div>
        {foreign && (
          <>
            <div className="nova-p4-field"><label htmlFor="f_country">Country</label><input id="f_country" className="form-control" value={country} onChange={(e) => setCountry(e.target.value)} /></div>
            <div className="nova-p4-field">
              <label htmlFor="f_tin">TIN</label>
              <input id="f_tin" className="form-control" value={tin} onChange={(e) => setTin(e.target.value)} placeholder="Enter the TIN, or the reason there is none" />
              {!tin && <input className="form-control" style={{ marginTop: 6 }} value={noTinReason} onChange={(e) => setNoTinReason(e.target.value)} placeholder="Reason there is no TIN" />}
            </div>
          </>
        )}
        <div className="nova-p4-field">
          <label htmlFor="f_us">US person?</label>
          <select id="f_us" className="form-select" value={usPerson ? 'yes' : 'no'} onChange={(e) => setUsPerson(e.target.value === 'yes')} disabled={!!pending}>
            <option value="no">No</option><option value="yes">Yes</option>
          </select>
        </div>
        <label style={{ display: 'flex', gap: 8, fontSize: '0.88rem' }}>
          <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} />
          I declare that the information above is true and complete, and I will tell the bank within 30 days if it changes.
        </label>
        <button type="button" className="nova-p4-primary" style={{ marginTop: 12 }} disabled={!!pending || !declared} onClick={() => void auth.start('NOVABANK', 'FATCA update')}>Update (OTP)</button>
      </div>
      <PaymentAuthSheets authState={auth.authState} approvalLine="FATCA update" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
    </div>
  );
};

// ============================================================ Favourites (§5.5)

export const Favourites = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Awaited<ReturnType<typeof getFavourites>> | null>(null);
  const load = () => { runBankClock(); getFavourites().then(setItems).catch(() => setItems([])); };
  useEffect(load, []);

  return (
    <div className="nova-p4-page">
      <Header title="Favourites" onBack={() => navigate('/profile/bank')} />
      <div className="nova-p4-banner blue">Favourites show as quick tiles at the top of the Pay tab. No OTP needed to manage them.</div>
      <ListState data={items} error={null} onRetry={load} empty={<span>No favourites yet. Star a payee, biller or frequent payment to pin it here.</span>}>
        {(list) => list.map((f) => (
          <div key={f.id} className="nova-p4-row" style={{ cursor: 'default' }}>
            <span className="nova-p4-row-icon"><FontAwesomeIcon icon={faStar} /></span>
            <span className="nova-p4-row-text">
              <span className="nova-p4-row-title">{f.label}</span>
              <span className="nova-p4-row-sub">{f.kind === 'PAYEE' ? 'Payee' : f.kind === 'UPI' ? 'Frequent payment' : 'Biller'} · {f.sub}</span>
            </span>
            <button type="button" className="nova-p4-link" onClick={() => { const n = window.prompt('Nickname (24 characters)', f.label); if (n != null) void renameFavourite(f.id, n).then(load); }}>Rename</button>
            <button type="button" className="nova-p4-link" style={{ color: '#c0392b' }} onClick={() => void removeFavourite(f.id).then(load)}>Remove</button>
          </div>
        ))}
      </ListState>
    </div>
  );
};

// ============================================================ Nominee (§5.9, read-only)

export const Nominee = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<Awaited<ReturnType<typeof accountNominee>> | null>(null);
  useEffect(() => { accountNominee().then(setState).catch(() => undefined); }, []);

  return (
    <div className="nova-p4-page">
      <Header title="View/Add Nominee Details" onBack={() => navigate('/profile/bank')} />
      {state && !state.recorded && <div className="nova-p4-empty">No nominee is recorded on this account.</div>}
      {state?.recorded && (
        <div className="nova-p4-card">
          <div className="nova-p4-moneyline muted"><span>Nominee</span><span>{state.name}</span></div>
          <div className="nova-p4-moneyline muted"><span>Relationship</span><span>{state.relationship}</span></div>
        </div>
      )}
      <div className="nova-p4-banner blue">{state?.note}</div>
    </div>
  );
};
